import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { scheduleService, participantsService } from "../../services";
import resultsService from "../../services/resultsService";
import resultsStreamService from "../../services/resultsStreamService";

const LiveResultsView = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const venue = searchParams.get("venue") || "Fläche A";
  
  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [results, setResults] = useState(new Map());
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updatedValues, setUpdatedValues] = useState(new Set()); // Track welche Werte sich geändert haben
  const previousPositionsRef = useRef(new Map()); // Track vorherige Positionen für Animationen

  useEffect(() => {
    loadInitialData();
  }, [venue]);

  // Lade initial alle Daten einmalig
  const loadInitialData = async () => {
    try {
      setLoading(true);
      await loadEventAndParticipants(true); // true = lade auch initial Ergebnisse
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lade Event und Teilnehmer (wird auch beim Polling verwendet)
  const loadEventAndParticipants = async (loadInitialResults = false) => {
    // Lade Schedule-Events für die Fläche
    const scheduleResult = await scheduleService.getAllScheduleEvents({ venue });
    const scheduleEvents = scheduleResult.success ? scheduleResult.data || [] : [];
    
    // Sortiere nach Startzeit
    scheduleEvents.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    
    // Finde aktuelles Event (Status "Aktiv")
    const activeEvent = scheduleEvents.find(e => e.status === 'Aktiv');
    
    // Prüfe ob sich die Gruppe geändert hat (verwende State-Setter mit Callback)
    setCurrentEvent(prevEvent => {
      const currentGroupId = prevEvent?.group?.id;
      const newGroupId = activeEvent?.group?.id;
      
      if (currentGroupId !== newGroupId) {
        // Neue Gruppe aktiv - lade Teilnehmer neu
        if (activeEvent && activeEvent.group) {
          participantsService.getAllParticipants().then(participantsResult => {
            if (participantsResult.success) {
              const allParticipants = participantsResult.data || [];
              const eventParticipants = allParticipants.filter(p => {
                if (!p.groups || !Array.isArray(p.groups)) return false;
                return p.groups.some(g => g.id === activeEvent.group.id);
              });
              
              // Sortiere nach Startnummer
              eventParticipants.sort((a, b) => {
                const aGroup = a.groups.find(g => g.id === activeEvent.group.id);
                const bGroup = b.groups.find(g => g.id === activeEvent.group.id);
                const aStartNumber = aGroup ? aGroup.startNumber : 999;
                const bStartNumber = bGroup ? bGroup.startNumber : 999;
                return aStartNumber - bStartNumber;
              });
              
              setParticipants(eventParticipants);
              
              // Lade initial Ergebnisse nur beim ersten Laden (SSE übernimmt dann die Updates)
              if (loadInitialResults) {
                loadResults(activeEvent.group.id);
              } else {
                // Beim Gruppenwechsel ohne initiales Laden: Setze Ergebnisse zurück
                // SSE wird die Updates liefern
                setResults(new Map());
              }
            }
          });
        } else {
          setParticipants([]);
          setResults(new Map());
        }
      }
      
      return activeEvent || null;
    });
    
    // Lade initial Ergebnisse nur wenn explizit angefordert (beim ersten Laden)
    if (loadInitialResults && activeEvent?.group?.id) {
      await loadResults(activeEvent.group.id);
    }
  };

  // Lade nur die Ergebnisse (wird regelmäßig aufgerufen)
  const loadResults = async (groupId) => {
    if (!groupId) return;
    
    try {
      const resultsResult = await resultsService.getResultsByGroup(groupId);
      if (resultsResult.success && resultsResult.data && Array.isArray(resultsResult.data)) {
        const existingResults = resultsResult.data;
        const resultsMap = new Map();
        
        existingResults.forEach(result => {
          const participantId = result.participantId || (result.nationId ? `nation-${result.nationId}` : null);
          if (participantId) {
            const judgeScores = [
              parseFloat(result.judgeScore1) || 0,
              parseFloat(result.judgeScore2) || 0,
              parseFloat(result.judgeScore3) || 0,
              parseFloat(result.judgeScore4) || 0,
              parseFloat(result.judgeScore5) || 0
            ];
            const penalty = parseFloat(result.penalty) || 0;
            const validScores = judgeScores.filter(score => score > 0);
            const averageScore = validScores.length > 0
              ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length
              : 0;
            const finalScore = averageScore + penalty;
            
            resultsMap.set(participantId, {
              judgeScores,
              penalty,
              finalScore: finalScore.toFixed(2),
              rank: result.rank || null,
              didNotStart: result.didNotStart || false,
              isLate: result.isLate || false
            });
          }
        });
        
        setResults(resultsMap);
      }
    } catch (error) {
      console.warn('Fehler beim Laden der Ergebnisse:', error);
    }
  };

  // Polling für Event-Änderungen (alle 30 Sekunden, um Gruppenwechsel zu erkennen)
  // Ergebnisse werden jetzt über SSE empfangen
  useEffect(() => {
    // Lade Event und Teilnehmer sofort
    loadEventAndParticipants();
    
    // Prüfe alle 30 Sekunden, ob sich das aktive Event geändert hat
    const interval = setInterval(async () => {
      await loadEventAndParticipants();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [venue]);

  // SSE-Verbindung für Live-Ergebnisse
  useEffect(() => {
    const groupId = currentEvent?.group?.id;
    
    if (!groupId) {
      // Keine aktive Gruppe - trenne alle Verbindungen
      resultsStreamService.disconnect();
      return;
    }

    // Handler für SSE-Updates
    const handleUpdate = (update) => {
      if (update.type === 'error') {
        console.error('SSE-Fehler:', update.message);
        return;
      }

      if (update.type === 'connected') {
        console.log('SSE-Verbindung hergestellt für Gruppe', update.groupId);
        return;
      }

      if (update.type === 'result-update') {
        // Einzelnes Result-Update verarbeiten
        console.log('SSE: Result-Update empfangen für Teilnehmer', update.result.participantId);
        const result = update.result;
        const participantId = result.participantId || (result.nationId ? `nation-${result.nationId}` : null);
        
        if (participantId) {
          // Stelle sicher, dass judgeScores immer ein Array ist
          const judgeScores = [
            parseFloat(result.judgeScore1) || 0,
            parseFloat(result.judgeScore2) || 0,
            parseFloat(result.judgeScore3) || 0,
            parseFloat(result.judgeScore4) || 0,
            parseFloat(result.judgeScore5) || 0
          ];
          const penalty = parseFloat(result.penalty) || 0;
          const validScores = judgeScores.filter(score => score > 0);
          const averageScore = validScores.length > 0
            ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length
            : 0;
          const finalScore = averageScore + penalty;
          
          setResults(prev => {
            const newMap = new Map(prev);
            const oldResult = prev.get(participantId);
            
            // Stelle sicher, dass oldResult.judgeScores ein Array ist für Vergleich
            const oldJudgeScores = oldResult?.judgeScores && Array.isArray(oldResult.judgeScores) 
              ? oldResult.judgeScores 
              : [null, null, null, null, null];
            
            // Markiere geänderte Werte für Animation
            const changedKeys = new Set();
            if (!oldResult || oldResult.finalScore !== finalScore.toFixed(2)) {
              changedKeys.add(`finalScore-${participantId}`);
            }
            judgeScores.forEach((score, idx) => {
              const oldScore = oldJudgeScores[idx];
              // Wenn alter Score null/undefined war und neuer Score > 0, markiere als geändert
              if (!oldResult || oldScore === undefined || oldScore === null || oldScore !== score) {
                changedKeys.add(`judgeScore${idx + 1}-${participantId}`);
              }
            });
            if (!oldResult || oldResult.penalty !== penalty) {
              changedKeys.add(`penalty-${participantId}`);
            }
            if (!oldResult || oldResult.rank !== (result.rank || null)) {
              changedKeys.add(`rank-${participantId}`);
            }
            
            if (changedKeys.size > 0) {
              setUpdatedValues(prev => {
                const newSet = new Set(prev);
                changedKeys.forEach(key => newSet.add(key));
                // Entferne nach Animation (500ms)
                setTimeout(() => {
                  setUpdatedValues(current => {
                    const updated = new Set(current);
                    changedKeys.forEach(key => updated.delete(key));
                    return updated;
                  });
                }, 500);
                return newSet;
              });
            }
            
            newMap.set(participantId, {
              judgeScores, // Immer ein Array
              penalty,
              finalScore: finalScore.toFixed(2),
              rank: result.rank || null,
              didNotStart: result.didNotStart || false,
              isLate: result.isLate || false
            });
            console.log('SSE: Ergebnisse aktualisiert für Teilnehmer', participantId, 'Final Score:', finalScore.toFixed(2));
            return newMap;
          });
          
          setLastUpdate(new Date());
        }
      } else if (update.type === 'rank-update') {
        // Rang-Updates für alle Teilnehmer verarbeiten
        console.log('SSE: Rang-Update empfangen für', update.results.length, 'Teilnehmer');
        setResults(prev => {
          const newMap = new Map(prev);
          
          update.results.forEach(result => {
            const participantId = result.participantId || (result.nationId ? `nation-${result.nationId}` : null);
            if (participantId) {
              const existing = newMap.get(participantId) || {};
              const oldRank = existing.rank;
              const newRank = result.rank || null;
              
              // Markiere Rang-Änderung für Animation
              if (oldRank !== newRank) {
                setUpdatedValues(prev => {
                  const newSet = new Set(prev);
                  newSet.add(`rank-${participantId}`);
                  setTimeout(() => {
                    setUpdatedValues(current => {
                      const updated = new Set(current);
                      updated.delete(`rank-${participantId}`);
                      return updated;
                    });
                  }, 500);
                  return newSet;
                });
              }
              
              newMap.set(participantId, {
                ...existing,
                rank: newRank
              });
            }
          });
          
          return newMap;
        });
        
        setLastUpdate(new Date());
      }
    };

    // Verbindung herstellen
    console.log(`SSE: Verbinde mit Gruppe ${groupId}`);
    resultsStreamService.connect(groupId, handleUpdate);

    // Cleanup bei Unmount oder Gruppenwechsel
    return () => {
      console.log(`SSE: Trenne Verbindung für Gruppe ${groupId}`);
      resultsStreamService.disconnectGroup(groupId);
    };
  }, [currentEvent?.group?.id]);

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  // Sortiere Teilnehmer: Normale Teilnehmer nach Endpunktzahl, dann DNS-Teilnehmer am Ende
  const sortedParticipants = useMemo(() => {
    const sorted = [...participants].sort((a, b) => {
      const aResult = results.get(a.id);
      const bResult = results.get(b.id);
      const aDidNotStart = aResult?.didNotStart || false;
      const bDidNotStart = bResult?.didNotStart || false;
      
      // DNS-Teilnehmer immer ganz am Ende
      if (aDidNotStart && !bDidNotStart) return 1;
      if (!aDidNotStart && bDidNotStart) return -1;
      
      // Normale Teilnehmer nach Endpunktzahl (absteigend) sortieren
      const aScore = aResult ? parseFloat(aResult.finalScore) : -1;
      const bScore = bResult ? parseFloat(bResult.finalScore) : -1;
      
      if (aScore !== bScore) {
        return bScore - aScore; // Höchste Punktzahl zuerst
      }
      
      // Bei gleicher Punktzahl nach Startnummer sortieren
      const aGroup = a.groups.find(g => g.id === currentEvent?.group?.id);
      const bGroup = b.groups.find(g => g.id === currentEvent?.group?.id);
      const aStartNumber = aGroup ? aGroup.startNumber : 999;
      const bStartNumber = bGroup ? bGroup.startNumber : 999;
      return aStartNumber - bStartNumber;
    });
    
    // Track Position-Änderungen für Animationen
    const currentPositions = new Map();
    sorted.forEach((p, index) => {
      currentPositions.set(p.id, index);
    });
    
    const prevPositions = previousPositionsRef.current;
    const hasPositionChanges = Array.from(currentPositions.entries()).some(([id, pos]) => {
      return prevPositions.get(id) !== undefined && prevPositions.get(id) !== pos;
    });
    
    if (hasPositionChanges) {
      // Positionen haben sich geändert - aktualisiere Ref nach kurzer Verzögerung für Animation
      setTimeout(() => {
        previousPositionsRef.current = currentPositions;
      }, 50);
    } else {
      previousPositionsRef.current = currentPositions;
    }
    
    return sorted;
  }, [participants, results, currentEvent?.group?.id]);
  
  // Berechne Position-Änderungen für Animationen
  const getPositionChange = (participantId) => {
    const prevPos = previousPositionsRef.current.get(participantId);
    const currentPos = sortedParticipants.findIndex(p => p.id === participantId);
    
    if (prevPos === undefined || prevPos === currentPos) {
      return null;
    }
    
    return {
      from: prevPos,
      to: currentPos,
      direction: currentPos < prevPos ? 'up' : 'down'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Lade Daten...</p>
        </div>
      </div>
    );
  }

  if (!currentEvent) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-clock text-slate-400 dark:text-slate-500 text-2xl"></i>
          </div>
          <h3 className="text-xl font-medium text-slate-900 dark:text-slate-100 mb-2">
            Kein aktives Event
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            Es läuft derzeit kein Event auf {venue}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-8">
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0.5;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0.5;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slideUp 0.5s ease-in-out;
        }
        .animate-slide-down {
          animation: slideDown 0.5s ease-in-out;
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                  {currentEvent.group?.name || currentEvent.category}
                </h1>
                <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                  <span>{formatDate(currentEvent.startTime)}</span>
                  <span>•</span>
                  <span>{formatTime(currentEvent.startTime)} - {formatTime(currentEvent.endTime)}</span>
                  <span>•</span>
                  <span>{venue}</span>
                  {lastUpdate && (
                    <>
                      <span>•</span>
                      <span className="text-xs">
                        <i className="fas fa-sync-alt mr-1"></i>
                        Aktualisiert: {formatTime(lastUpdate.toISOString())}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {lastUpdate && (
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    <i className="fas fa-clock mr-1"></i>
                    {formatTime(lastUpdate.toISOString())}
                  </div>
                )}
                <div className="bg-green-100 dark:bg-green-900/30 px-4 py-2 rounded-full">
                  <span className="text-green-700 dark:text-green-300 font-medium">
                    LIVE
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ergebnisse Tabelle */}
        {sortedParticipants.length > 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Rang
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Athlet
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      R1
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      R2
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      R3
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      R4
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      R5
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Strafpunkte
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Endpunktzahl
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {sortedParticipants.map((participant, index) => {
                    const participantResult = results.get(participant.id);
                    // Stelle sicher, dass judgeScores immer ein Array ist
                    const judgeScores = participantResult?.judgeScores && Array.isArray(participantResult.judgeScores) 
                      ? participantResult.judgeScores 
                      : [null, null, null, null, null];
                    const nationCode = participant.nation?.code || participant.nationCode || 'DE';
                    const participantGroup = participant.groups.find(g => g.id === currentEvent.group.id);
                    const startNumber = participantGroup ? participantGroup.startNumber : null;
                    const didNotStart = participantResult?.didNotStart || false;
                    const isLate = participantResult?.isLate || false;
                    
                    // Berechne aktuellen Rang basierend auf Position im sortierten Array
                    // DNS-Teilnehmer bekommen keinen Rang, sondern "DNS"
                    // Zähle nur normale Teilnehmer für Rang-Berechnung
                    const actualRank = didNotStart ? null : (sortedParticipants.slice(0, index + 1).filter(p => {
                      const pResult = results.get(p.id);
                      return !(pResult?.didNotStart || false);
                    }).length);
                    
                    // Prüfe Position-Änderung für Animation
                    const positionChange = getPositionChange(participant.id);
                    const isMoving = positionChange !== null;
                    const animationClass = isMoving 
                      ? (positionChange.direction === 'up' ? 'animate-slide-up' : 'animate-slide-down')
                      : '';
                    
                    return (
                      <tr 
                        key={participant.id} 
                        className={`hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-all duration-500 ease-in-out ${animationClass} ${
                          didNotStart ? 'opacity-50 grayscale' :
                          actualRank === 1 ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                          actualRank === 2 ? 'bg-slate-50 dark:bg-slate-700/30' :
                          actualRank === 3 ? 'bg-orange-50 dark:bg-orange-900/20' :
                          ''
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          {didNotStart ? (
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                              DNS
                            </span>
                          ) : (
                            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-bold ${
                              actualRank === 1 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                              actualRank === 2 ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300' :
                              actualRank === 3 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                              'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                            }`}>
                              {actualRank}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {startNumber || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <img
                              src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                              alt={participant.nation?.name || participant.nation}
                              className="w-8 h-8 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                            />
                            <div>
                              <div className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                {participant.name}
                                {didNotStart && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                    <i className="fas fa-times-circle mr-1"></i>
                                    DNS
                                  </span>
                                )}
                                {isLate && !didNotStart && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                    <i className="fas fa-clock mr-1"></i>
                                    Verspätet
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-slate-500 dark:text-slate-400">
                                {participant.nation?.name || participant.nation}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium text-slate-900 dark:text-slate-100 transition-all duration-300 ${
                            updatedValues.has(`judgeScore1-${participant.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded' : ''
                          }`}>
                            {didNotStart ? '-' : (judgeScores[0] !== null && judgeScores[0] !== undefined ? judgeScores[0].toFixed(2) : '-')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium text-slate-900 dark:text-slate-100 transition-all duration-300 ${
                            updatedValues.has(`judgeScore2-${participant.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded' : ''
                          }`}>
                            {didNotStart ? '-' : (judgeScores[1] !== null && judgeScores[1] !== undefined ? judgeScores[1].toFixed(2) : '-')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium text-slate-900 dark:text-slate-100 transition-all duration-300 ${
                            updatedValues.has(`judgeScore3-${participant.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded' : ''
                          }`}>
                            {didNotStart ? '-' : (judgeScores[2] !== null && judgeScores[2] !== undefined ? judgeScores[2].toFixed(2) : '-')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium text-slate-900 dark:text-slate-100 transition-all duration-300 ${
                            updatedValues.has(`judgeScore4-${participant.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded' : ''
                          }`}>
                            {didNotStart ? '-' : (judgeScores[3] !== null && judgeScores[3] !== undefined ? judgeScores[3].toFixed(2) : '-')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium text-slate-900 dark:text-slate-100 transition-all duration-300 ${
                            updatedValues.has(`judgeScore5-${participant.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded' : ''
                          }`}>
                            {didNotStart ? '-' : (judgeScores[4] !== null && judgeScores[4] !== undefined ? judgeScores[4].toFixed(2) : '-')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium text-slate-900 dark:text-slate-100 transition-all duration-300 ${
                            updatedValues.has(`penalty-${participant.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded' : ''
                          }`}>
                            {didNotStart ? '-' : (participantResult?.penalty !== undefined && participantResult.penalty !== null ? (participantResult.penalty > 0 ? `+${participantResult.penalty.toFixed(2)}` : participantResult.penalty.toFixed(2)) : '-')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className={`text-xl font-bold text-slate-900 dark:text-slate-100 transition-all duration-300 ${
                            updatedValues.has(`finalScore-${participant.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded' : ''
                          }`}>
                            {didNotStart ? '0.00' : (participantResult?.finalScore !== undefined && participantResult.finalScore !== null ? participantResult.finalScore : '-')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-users text-slate-400 dark:text-slate-500 text-xl"></i>
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                Keine Teilnehmer gefunden
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Für dieses Event wurden noch keine Teilnehmer gefunden.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveResultsView;


